/**
 * The `positioning` section — Positioning & liquidity (`docs/plans/macro.md`;
 * the spec is `docs/mockups/macro-v2.html` § 05, `glFund` + `glDG`).
 *
 * **Funding stress** (the cause: SOFR − IORB over the reverse-repo cushion, the
 * 20-day average's state, the widest day labelled) above the **crowded long /
 * short spread** (the effect: our 13F basket minus most shorted, GVIP's version
 * faint behind it, unwind days as dots, the drawdown from peak as a band under
 * it), on **one date axis**, then the four legs as cards.
 *
 * The σ control re-strikes unwind days on the client from the server's `moZ`
 * (`lib/macro-positioning.ts`) and is persisted as `PositioningConfig.sigma`.
 * The hover line is view state (D8): one pointer date, drawn on both charts.
 *
 * Drawn as SVG, as the mock is: two fixed-shape charts of a few hundred points
 * with labels inside them, not a SciChart surface — the chart engine is for the
 * contract's chart groups. Chrome reads `var(--qf-*)`; the renderer-only tokens
 * (`grid`, `axis`, `series2` for the RRP area) come from `useTokenResolver`.
 *
 * Admin only: `MacroSectionHost` refuses anyone else before this mounts, and
 * `/macro/positioning` is `admin_only` on the server as well.
 */

import { useEffect, useMemo, useState, type CSSProperties, type PointerEvent, type ReactNode } from "react";
import { loadPositioning, type Positioning } from "../../api/macro-positioning";
import {
  SIGMAS,
  dayMs,
  effectiveSigma,
  fundingState,
  indexAtOrBefore,
  legSummary,
  linePath,
  quarterTicks,
  sharedAxis,
  signed1,
  spreadState,
  type SharedAxis,
  type Sigma,
} from "../../lib/macro-positioning";
import { useTokenResolver } from "../theme";
import type { MacroSectionProps } from "./types";
import { useWidth } from "./use-width";
import { macroScale } from "../../lib/macro-sections";

/** The app's own font (Inter), not a monospace (owner, 2026-11-02); numbers line up via tabular-nums on the host. */
const MONO = "inherit";
/** viewBox width before a measurement (jsdom never measures). */
const W0 = 1000;
/**
 * The widths below which each chart's one-line key is broken in two — half
 * width, beside Safe-haven correlations (owner, 2026-11-02). The keys are SVG
 * text, which never wraps; at 10.5px they are ~450px and ~880px long.
 */
const FUNDING_KEY_ONE_LINE = 560;
const SPREAD_KEY_ONE_LINE = 960;
const L = 44;
/** Right padding: both charts share L and this, so their dates line up. */
const RPAD = 12;

type Load = { state: "loading" } | { state: "none" } | { state: "ok"; data: Positioning };

export default function PositioningSection({ config, onConfig }: MacroSectionProps<"positioning">) {
  const [load, setLoad] = useState<Load>({ state: "loading" });
  const [localSigma, setLocalSigma] = useState<number | undefined>(config.sigma);
  // The stacked layout's drag-to-resize: chart heights times this
  // (`lib/macro-sections.ts`); widths stay measured, text 1:1.
  const scale = macroScale(config);
  const [hoverT, setHoverT] = useState<number | null>(null);

  useEffect(() => setLocalSigma(config.sigma), [config.sigma]);

  useEffect(() => {
    let live = true;
    loadPositioning().then((data) => {
      if (live) setLoad(data ? { state: "ok", data } : { state: "none" });
    });
    return () => {
      live = false;
    };
  }, []);

  const data = load.state === "ok" ? load.data : null;
  const sigma = effectiveSigma(localSigma, data?.params.unwindSigma);
  const axis = useMemo(() => (data ? sharedAxis(data.degross, data.funding) : null), [data]);

  const pick = (s: Sigma) => {
    setLocalSigma(s);
    onConfig?.({ ...config, sigma: s });
  };

  return (
    <div data-macro-kind="positioning" style={root}>
      <div style={head}>
        <span style={lbl}>Unwind day: momentum below</span>
        <div role="group" aria-label="Unwind σ" style={seg}>
          {SIGMAS.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={s === sigma}
              onClick={() => pick(s)}
              style={s === sigma ? segOn : segBtn}
            >
              −{s}σ
            </button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        {data && axis && (
          <span style={{ ...dim, fontSize: 12 }}>
            daily · {new Date(axis.d0).toISOString().slice(0, 10)} → {new Date(axis.d1).toISOString().slice(0, 10)}
            {data.builtAt ? ` · built ${data.builtAt.slice(0, 10)}` : ""}
          </span>
        )}
      </div>
      <div style={body}>
        {load.state === "loading" && <div style={note}>Loading positioning…</div>}
        {load.state === "none" && (
          <div role="note" style={note}>
            The positioning numbers are not built yet. They come from the nightly macro job.
          </div>
        )}
        {data && data.absent.length > 0 && (
          <div role="note" style={{ ...note, color: "var(--qf-warn)", textAlign: "left" }}>
            Missing inputs: {data.absent.join(", ")}. The lines that need them are empty.
          </div>
        )}
        {data && axis && (
          <>
            <FundingChart data={data} axis={axis} hoverT={hoverT} onHover={setHoverT} scale={scale} />
            <SpreadChart data={data} axis={axis} sigma={sigma} hoverT={hoverT} onHover={setHoverT} scale={scale} />
          </>
        )}
        {data && !axis && <div style={note}>No days to draw yet.</div>}
      </div>
    </div>
  );
}

/**
 * The shared date axis plus the drawn width. Each chart's viewBox is its measured
 * CSS width, so one user unit is one pixel: x stretches, text stays 9-11px.
 */
type Axis = SharedAxis & { w: number };

interface ChartProps {
  data: Positioning;
  axis: SharedAxis;
  hoverT: number | null;
  onHover: (t: number | null) => void;
  /** Chart height times this (the stacked layout's resize). */
  scale: number;
}

const rOf = (axis: Axis) => axis.w - RPAD;
const xOf = (axis: Axis) => (t: number) => L + ((t - axis.d0) / (axis.d1 - axis.d0)) * (rOf(axis) - L);

/** The pointer's date on the shared axis, from a pointer over an `axis.w`-wide viewBox. */
export function pointerT(e: Pick<PointerEvent<SVGSVGElement>, "clientX" | "currentTarget">, axis: Axis): number | null {
  const box = e.currentTarget.getBoundingClientRect();
  if (box.width <= 0) return null;
  const x = ((e.clientX - box.left) / box.width) * axis.w;
  const R = rOf(axis);
  if (x < L || x > R) return null;
  return axis.d0 + ((x - L) / (R - L)) * (axis.d1 - axis.d0);
}

function XAxis({ axis, top, bottom, height }: { axis: Axis; top: number; bottom: number; height: number }) {
  const resolve = useTokenResolver();
  const X = xOf(axis);
  return (
    <>
      {quarterTicks(axis.d0, axis.d1).map(({ t, label }) => (
        <g key={t}>
          <line x1={X(t)} x2={X(t)} y1={top} y2={bottom} stroke={resolve("grid")} />
          <text x={X(t) + 3} y={height - 5} style={svgSmall}>
            {label}
          </text>
        </g>
      ))}
    </>
  );
}

function HoverLine({ axis, hoverT, top, bottom }: { axis: Axis; hoverT: number | null; top: number; bottom: number }) {
  if (hoverT == null) return null;
  const x = xOf(axis)(hoverT);
  return <line data-hover-line x1={x} x2={x} y1={top} y2={bottom} style={{ stroke: "var(--qf-text-secondary)" }} strokeDasharray="3 3" />;
}

function FundingChart({ data, axis: base, hoverT, onHover, scale }: ChartProps) {
  const resolve = useTokenResolver();
  const [svgRef, W] = useWidth(W0);
  const axis: Axis = { ...base, w: W };
  const R = rOf(axis);
  const H = Math.round(180 * scale);
  const T = 22;
  const B = H - 22;
  const f = data.funding;
  const from = axis.fundingFrom;
  const st = fundingState(f, data.params, from);
  const dates = f.date.slice(from);
  const sp = f.spreadBp.slice(from);
  const rr = f.rrp.slice(from);
  const X = xOf(axis);
  const xs = dates.map((d) => X(dayMs(d)));
  const spVals = sp.filter((v): v is number => v != null);
  const lo = Math.min(-10, ...spVals);
  const hi = Math.max(10, ...spVals);
  const Y = (v: number) => T + ((hi - v) / (hi - lo)) * (B - T);
  const rrVals = rr.filter((v): v is number => v != null);
  const rmx = (rrVals.length ? Math.max(...rrVals) : 1) * 1.05 || 1;
  const YR = (v: number) => B - (v / rmx) * (B - T);
  const thr = data.params.fundingTightBp;
  const rrpArea = rrVals.length
    ? `M${xs[0]},${B}` + rr.map((v, i) => `L${(xs[i] ?? L).toFixed(1)},${YR(v ?? 0).toFixed(1)}`).join("") + `L${xs[xs.length - 1]},${B}Z`
    : "";
  const hi_ = hoverT == null ? -1 : indexAtOrBefore(dates, hoverT);
  const lastI = sp.length - 1;
  const rrpFmt = (v: number) => `$${v < 10 ? v.toFixed(1) : Math.round(v)}B`;
  const stColor = STATE_COLOR[st.state];

  return (
    <div style={chartbox} data-chart="funding">
      <div style={hht}>
        Funding stress <span style={{ ...dim, textTransform: "none", letterSpacing: 0 }}>the cause</span>
      </div>
      <div style={hhh}>
        <span>
          <b>SOFR − IORB</b> <span style={dim}>· is cash scarce? · with the reverse-repo cushion behind it · daily</span>
        </span>
        <span style={{ flex: 1 }} />
        <span style={mono}>
          {data.params.fundingAvgDays}d avg{" "}
          <b style={{ color: stColor }}>
            {st.avg == null ? "—" : `${signed1(st.avg)} bp`}
          </b>
          {st.lastDate && st.last != null && (
            <>
              {" "}
              · {st.lastDate} <b>{signed1(st.last)} bp</b>
            </>
          )}
          {st.rrp != null && (
            <>
              {" "}
              · RRP <b>{rrpFmt(st.rrp)}</b>
            </>
          )}
        </span>
        <span data-state={st.state} style={STATE_PILL[st.state]}>
          {st.state === "nodata" ? "no data" : st.state}
        </span>
      </div>
      <div style={{ ...hhr, color: stColor }}>
        {st.state === "tight"
          ? "⚠ Cash is scarce: SOFR trades above the Fed's floor, and the reverse-repo cushion is gone"
          : st.state === "calm"
            ? "✓ Cash is plentiful: SOFR trades below the Fed's floor"
            : `No data: the ${data.params.fundingAvgDays}-day average of SOFR − IORB is missing`}
      </div>
      <Readout>
        {hi_ >= 0 && (
          <>
            {dates[hi_]} · SOFR − IORB <b>{sp[hi_] == null ? "—" : `${signed1(sp[hi_]!)} bp`}</b>
            {rr[hi_] != null && (
              <>
                {" "}
                · RRP <b>{rrpFmt(rr[hi_]!)}</b>
              </>
            )}
          </>
        )}
      </Readout>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: "block", touchAction: "pan-y" }}
        onPointerMove={(e) => onHover(pointerT(e, axis))}
        onPointerLeave={() => onHover(null)}
      >
        <rect x={L} y={Y(hi)} width={R - L} height={Math.max(0, Y(thr) - Y(hi))} style={{ fill: "var(--qf-negative)" }} fillOpacity={0.06} />
        {rrpArea && <path d={rrpArea} fill={resolve("series2")} fillOpacity={0.16} />}
        {[-10, 0, 10, 20, 30]
          .filter((v) => v >= lo && v <= hi)
          .map((v) => (
            <g key={v}>
              <line x1={L} x2={R} y1={Y(v)} y2={Y(v)} stroke={resolve(v ? "grid" : "axis")} />
              <text x={L - 6} y={Y(v) + 3} textAnchor="end" style={svgText}>
                {v > 0 ? "+" : ""}
                {v}
              </text>
            </g>
          ))}
        {[100, 250, 500, 1000, 2000]
          .filter((v) => v <= rmx)
          .slice(-2)
          .map((v) => (
            <text key={v} x={L + 6} y={YR(v) - 3} style={{ ...svgSmall, fill: resolve("series2") }}>
              RRP ${v}B
            </text>
          ))}
        <XAxis axis={axis} top={T} bottom={B} height={H} />
        <path d={linePath(xs, sp, Y)} fill="none" style={{ stroke: "var(--qf-accent)" }} strokeWidth={2.3} />
        {st.peak != null && sp[st.peak] != null && (
          <text x={(xs[st.peak] ?? L) - 6} y={Y(sp[st.peak]!) + 4} textAnchor="end" style={{ ...svgSmall, fill: "var(--qf-negative)" }}>
            {dates[st.peak]} {signed1(sp[st.peak]!)} bp
          </text>
        )}
        {lastI >= 0 && sp[lastI] != null && (
          <circle
            cx={xs[lastI]}
            cy={Y(sp[lastI]!)}
            r={4}
            style={{ fill: stColor }}
          />
        )}
        <HoverLine axis={axis} hoverT={hoverT} top={T} bottom={B} />
        {W >= FUNDING_KEY_ONE_LINE ? (
          <text data-key x={R - 6} y={12} textAnchor="end" style={svgSmall}>
            blue = SOFR − IORB, bp (left) · purple = overnight reverse repo, $B (shaded)
          </text>
        ) : (
          <>
            <text data-key x={R - 6} y={10} textAnchor="end" style={svgSmall}>
              blue = SOFR − IORB, bp (left)
            </text>
            <text data-key x={R - 6} y={21} textAnchor="end" style={svgSmall}>
              purple = overnight reverse repo, $B (shaded)
            </text>
          </>
        )}
      </svg>
    </div>
  );
}

function SpreadChart({ data, axis: base, sigma, hoverT, onHover, scale }: ChartProps & { sigma: number }) {
  const resolve = useTokenResolver();
  const [svgRef, W] = useWidth(W0);
  const axis: Axis = { ...base, w: W };
  const R = rOf(axis);
  const dg = data.degross;
  const p = data.params;
  const st = useMemo(() => spreadState(dg, "13f", sigma, p), [dg, sigma, p]);
  const H = Math.round(250 * scale);
  const T = 10;
  const M1 = 150;
  const B = H - 22;
  const X = xOf(axis);
  const xs = dg.date.map((d) => X(dayMs(d)));
  const all = [...dg.idx, ...dg.gIdx].filter((v): v is number => v != null);
  const lo = all.length ? Math.min(...all) : 99;
  const hi = all.length ? Math.max(...all) : 101;
  const span = hi - lo || 1;
  const Y = (v: number) => T + ((hi - v) / span) * (M1 - T - 6);
  const dlo = Math.min(-1, (st.worst ?? 0) * 1.1);
  const YD = (v: number) => M1 + 6 + (v / dlo) * (B - M1 - 6);
  const n = dg.date.length;
  const ddArea = n
    ? `M${xs[0]},${M1 + 6}` + dg.dd.map((v, i) => `L${(xs[i] ?? L).toFixed(1)},${YD(v ?? 0).toFixed(1)}`).join("") + `L${xs[n - 1]},${M1 + 6}Z`
    : "";
  const cur = st.fromPeak;
  const curColor = cur == null ? "var(--qf-text)" : cur < -5 ? "var(--qf-negative)" : cur < -2 ? "var(--qf-warn)" : "var(--qf-positive)";
  const lastG = (() => {
    for (let i = n - 1; i >= 0; i--) if (dg.gIdx[i] != null) return i;
    return -1;
  })();
  const hv = hoverT == null ? -1 : indexAtOrBefore(dg.date, hoverT);
  const unwindSet = useMemo(() => new Set(st.unwind), [st.unwind]);
  const pct = (v: number | null | undefined) => (v == null ? "—" : `${signed1(v)}%`);

  return (
    <div style={chartbox} data-chart="spread">
      <div style={hht}>
        Crowded long / short spread{" "}
        <span style={{ ...dim, textTransform: "none", letterSpacing: 0 }}>the effect · same time axis</span>
      </div>
      <div style={hhh}>
        <span>
          <b>Crowded long / short spread</b> <span style={dim}>· hedge-fund favourites minus most shorted, daily</span>
        </span>
        <span style={{ flex: 1 }} />
        <span style={mono}>
          from peak <b style={{ color: curColor }}>{cur == null ? "—" : `${cur.toFixed(1)}%`}</b> · unwind days, last{" "}
          {p.unwindRecentDays}{" "}
          <b data-unwind-recent style={{ color: st.recent ? "var(--qf-negative)" : undefined }}>
            {st.recent}
          </b>{" "}
          · window worst <b>{st.worst == null ? "—" : `${st.worst.toFixed(1)}%`}</b>
        </span>
        <span data-state={st.state} style={STATE_PILL[st.state]}>
          {st.state === "nodata" ? "no data" : st.state === "degrossing" ? "de-grossing" : "normal"}
        </span>
      </div>
      <div style={{ ...hhr, color: STATE_COLOR[st.state] }}>
        {st.state === "degrossing"
          ? `⚠ Crowded longs are being sold and crowded shorts covered${st.deepest ? ": the deepest in the window" : ""}`
          : st.state === "normal"
            ? "✓ No sign of a forced unwind"
            : "No data: the spread's drawdown from peak is missing"}
      </div>
      <Readout>
        {hv >= 0 && (
          <>
            {dg.date[hv]} · spread <b>{dg.idx[hv] == null ? "—" : dg.idx[hv]!.toFixed(1)}</b> · from peak{" "}
            <b>{pct(dg.dd[hv])}</b> · longs <b>{pct(dg.lg[hv])}</b> · GVIP <b>{pct(dg.gv[hv])}</b> · shorts{" "}
            <b>{pct(dg.sh[hv])}</b> · momentum <b>{pct(dg.mo[hv])}</b>
            {unwindSet.has(hv) && <b style={{ color: "var(--qf-negative)" }}> · unwind day</b>}
          </>
        )}
      </Readout>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: "block", touchAction: "pan-y" }}
        onPointerMove={(e) => onHover(pointerT(e, axis))}
        onPointerLeave={() => onHover(null)}
      >
        <XAxis axis={axis} top={T} bottom={B} height={H} />
        <path d={linePath(xs, dg.gIdx, Y)} fill="none" style={{ stroke: "var(--qf-warn)" }} strokeOpacity={0.45} strokeWidth={1.2} />
        {lastG >= 0 && (
          <text x={(xs[lastG] ?? L) - 4} y={Y(dg.gIdx[lastG]!) - 8} textAnchor="end" style={{ ...svgSmall, fill: "var(--qf-warn)" }} fillOpacity={0.7}>
            GVIP − most shorted
          </text>
        )}
        <path d={linePath(xs, dg.idx, Y)} fill="none" style={{ stroke: "var(--qf-accent)" }} strokeWidth={2.5} />
        {st.unwind.map((i) =>
          dg.idx[i] == null ? null : (
            <circle key={i} data-unwind cx={xs[i]} cy={Y(dg.idx[i]!)} r={3.5} style={{ fill: "var(--qf-negative)" }}>
              <title>
                {`${dg.date[i]} · unwind day: momentum ${(dg.mo[i] ?? 0).toFixed(1)}%, longs ${(dg.lg[i] ?? 0).toFixed(1)}% vs shorts ${signed1(dg.sh[i] ?? 0)}%`}
              </title>
            </circle>
          ),
        )}
        <line x1={L} x2={R} y1={M1 + 6} y2={M1 + 6} stroke={resolve("axis")} />
        {ddArea && <path d={ddArea} style={{ fill: "var(--qf-negative)" }} fillOpacity={0.35} />}
        <text x={L - 6} y={M1 + 10} textAnchor="end" style={svgText}>
          0
        </text>
        <text x={L - 6} y={YD(dlo) + 3} textAnchor="end" style={svgText}>
          {Math.round(dlo)}%
        </text>
        <text x={L + 4} y={M1 + 18} style={svgSmall}>
          drawdown from peak
        </text>
        <HoverLine axis={axis} hoverT={hoverT} top={T} bottom={B} />
        {W >= SPREAD_KEY_ONE_LINE ? (
          <text data-key x={L + 4} y={T + 10} style={svgSmall}>
            spread index (our 13F basket − most shorted; faint: GVIP − most shorted) · ● unwind day: momentum below −{sigma}σ
            and longs trail shorts by more than {p.unwindSpreadPct}%
          </text>
        ) : (
          <>
            <text data-key x={L + 4} y={T + 10} style={svgSmall}>
              spread index (our 13F basket − most shorted; faint: GVIP − most shorted)
            </text>
            <text data-key x={L + 4} y={T + 23} style={svgSmall}>
              ● unwind day: momentum below −{sigma}σ and longs trail shorts by more than {p.unwindSpreadPct}%
            </text>
          </>
        )}
      </svg>
      <div style={legs}>
        <Leg name="Crowded longs · our 13F basket" values={dg.lg} why={`Top ${p.basketSize} by hedge-fund ownership (13F), rebuilt quarterly`} />
        <Leg
          name="Crowded longs · GVIP (Goldman Sachs Hedge Fund VIP)"
          values={dg.gv}
          why="GVIP vs SPY: Goldman's 50 hedge-fund favourites from 13F, rebuilt quarterly"
        />
        <Leg name="Most shorted" values={dg.sh} why={`Top ${p.basketSize} by days to cover (short interest), rebuilt twice a month`} />
        <Leg name="Momentum L/S" values={dg.mo} why="Top minus bottom 10% by 12-month return, from our bars" />
      </div>
    </div>
  );
}

function Leg({ name, values, why }: { name: string; values: readonly (number | null)[]; why: string }) {
  const s = legSummary(values);
  const color = s.total == null ? NEUTRAL : s.total >= 0 ? "var(--qf-positive)" : "var(--qf-negative)";
  const lo = s.cum.length ? Math.min(...s.cum) : 0;
  const hi = s.cum.length ? Math.max(...s.cum) : 0;
  const d = s.cum
    .map((x, i) => `${i ? "L" : "M"}${((i * 200) / Math.max(1, s.cum.length - 1)).toFixed(1)},${(28 - ((x - lo) / (hi - lo || 1)) * 26).toFixed(1)}`)
    .join("");
  return (
    <div style={legCard} data-leg={name} data-state={s.total == null ? "nodata" : undefined}>
      <div style={legKey}>{name}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontFamily: MONO, fontSize: 17, fontWeight: 600, color }}>
          {s.total == null ? "no data" : `${signed1(s.total)}%`}
        </span>
        <span style={{ ...dim, fontSize: 11.5 }}>{s.total == null ? "no days in the last 20" : "20 days vs SPY"}</span>
      </div>
      <svg viewBox="0 0 200 30" preserveAspectRatio="none" style={{ width: "100%", height: 30, display: "block" }}>
        {s.total != null && <path d={d} fill="none" style={{ stroke: color }} strokeWidth={2.5} vectorEffect="non-scaling-stroke" />}
      </svg>
      <div style={{ ...dim, fontSize: 11.5 }}>{why}</div>
    </div>
  );
}

/** One line under a chart's header: the hovered day's numbers, or blank (kept at its height so nothing jumps). */
function Readout({ children }: { children?: ReactNode }) {
  return <div style={{ ...mono, color: "var(--qf-text-secondary)", padding: "0 12px", minHeight: 18 }}>{children}</div>;
}

const root: CSSProperties = {
  width: "100%",
  height: "100%",
  overflow: "auto",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  fontSize: 14,
};
const head: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 8,
  padding: "7px 12px",
  background: "var(--qf-surface-alt)",
  borderBottom: "1px solid var(--qf-border)",
};
const body: CSSProperties = { display: "flex", flexDirection: "column", gap: 12, padding: 12 };
const lbl: CSSProperties = { fontFamily: MONO, fontSize: 11.5, color: "var(--qf-text-muted)" };
const dim: CSSProperties = { color: "var(--qf-text-muted)" };
const mono: CSSProperties = { fontFamily: MONO, fontSize: 12.5 };
const seg: CSSProperties = { display: "flex", border: "1px solid var(--qf-border)", borderRadius: 5, overflow: "hidden" };
const segBtn: CSSProperties = {
  padding: "4px 9px",
  fontFamily: MONO,
  fontSize: 11.5,
  color: "var(--qf-text-secondary)",
  background: "none",
  border: 0,
  cursor: "pointer",
};
const segOn: CSSProperties = { ...segBtn, background: "var(--qf-selection)", color: "var(--qf-accent)" };
const note: CSSProperties = { ...dim, fontSize: 13, padding: "8px 4px", textAlign: "center" };
const chartbox: CSSProperties = { background: "var(--qf-background)", border: "1px solid var(--qf-border)", borderRadius: 5 };
const hht: CSSProperties = {
  fontFamily: MONO,
  fontSize: 11.5,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--qf-accent)",
  padding: "10px 12px 0",
};
const hhh: CSSProperties = { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 12px 0", fontSize: 13.5 };
const hhr: CSSProperties = { fontSize: 12.5, padding: "1px 12px 2px" };
const pill: CSSProperties = {
  fontFamily: MONO,
  fontSize: 11,
  padding: "1px 7px",
  borderRadius: 99,
  border: "1px solid var(--qf-border)",
};
const pillOk: CSSProperties = { ...pill, color: "var(--qf-positive)", borderColor: "var(--qf-positive)" };
const pillBad: CSSProperties = { ...pill, color: "var(--qf-negative)", borderColor: "var(--qf-negative)" };
/** Missing inputs are grey, never the green of an all-clear. */
const NEUTRAL = "var(--qf-text-muted)";
const pillNone: CSSProperties = { ...pill, color: NEUTRAL, borderColor: NEUTRAL };
type HeadState = "tight" | "calm" | "degrossing" | "normal" | "nodata";
const STATE_PILL: Record<HeadState, CSSProperties> = {
  tight: pillBad,
  degrossing: pillBad,
  calm: pillOk,
  normal: pillOk,
  nodata: pillNone,
};
const STATE_COLOR: Record<HeadState, string> = {
  tight: "var(--qf-negative)",
  degrossing: "var(--qf-negative)",
  calm: "var(--qf-positive)",
  normal: "var(--qf-positive)",
  nodata: NEUTRAL,
};
const svgText: CSSProperties = { fontFamily: MONO, fontSize: 11, fill: "var(--qf-text-muted)" };
const svgSmall: CSSProperties = { ...svgText, fontSize: 10.5 };
const legs: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  gap: 8,
  padding: "8px 12px 12px",
};
const legCard: CSSProperties = {
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  padding: "6px 9px",
};
const legKey: CSSProperties = {
  fontFamily: MONO,
  fontSize: 10.5,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
};
